import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core'
import { NgClass } from '@angular/common'
import { finalize, merge, takeUntil } from 'rxjs'

import { DotFlashingComponent, ResponsiveComponent, SearchState } from '@nx-superprep/shared'
import { DateOnly, _isNewDesign, distantPast, durationFormatter, log } from '@nx-superprep/utils'
import { StudyService, DayPlan, DayPlanModule, OverallStats, DayPlanAndModules } from '@nx-superprep/backend/firebase'
import { DatePickerComponent } from '../date-picker/date-picker.component'
import { DrawerComponent, DrawerState } from '../drawer/drawer.component'
import { StatusComponent } from './status/status.component'
import { CompletionState } from './status/completion-state'
import { LessonDetailComponent } from '../lesson/detail/detail.component'
import { LessonTile, LessonTileService } from '@nx-superprep/study'
import { NavigationBarState } from '../navigationbar/navigationbar.component'
import { BsModalService } from 'ngx-bootstrap/modal'
import { TourMatMenuModule } from 'ngx-ui-tour-md-menu'

import { MatCardModule } from '@angular/material/card'
import { MatIconModule } from '@angular/material/icon'
import { trigger, transition, style, animate } from '@angular/animations'

@Component({
  selector: 'pop-ui-home',
  standalone: true,
  templateUrl: './home.component.html',
  styleUrls: ['./home.component.scss'],
  imports: [
    NgClass,
    DatePickerComponent,
    DrawerComponent,
    StatusComponent,
    DotFlashingComponent,
    LessonDetailComponent,
    TourMatMenuModule,
    MatCardModule, MatIconModule,
  ],
  providers: [BsModalService],
  animations: [
    trigger('fadeInOut', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('150ms', style({ opacity: 1 }))
      ]),
      transition(':leave', [
        animate('150ms', style({ opacity: 0 }))
      ])
    ]),
    trigger('fastFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('100ms', style({ opacity: 1 }))
      ])
    ]),
    trigger('slideInOut', [
      transition(':enter', [
        style({ transform: 'translateY(20px)', opacity: 0 }),
        animate('200ms ease-out', style({ transform: 'translateY(0)', opacity: 1 }))
      ])
    ])
  ]
})
export class HomeComponent extends ResponsiveComponent implements OnInit, OnDestroy {
  private dayPlanCache = new Map<string, DayPlanAndModules>();
  private _isLoading = new DateOnly(distantPast) // Variable to track if data is currently being loaded

  submitted = false // Boolean to track form submission state

  private _activeDate: DateOnly = new DateOnly()
  @Input()
  get activeDate() { return this._activeDate }
  set activeDate(value) {
    this._activeDate = new DateOnly(value)
    this.getData(this._activeDate)
    this.activeDateChange.emit(this._activeDate)
  }
  @Output() activeDateChange = new EventEmitter<DateOnly>()

  private _state!: DrawerState
  @Input()
  get state() { return this._state }
  set state(value) {
    this._state = value
    this.stateChange.emit(this._state)
  }
  @Output() stateChange = new EventEmitter<DrawerState>()

  private _completionState!: CompletionState
  @Input()
  get completionState() { return this._completionState }
  set completionState(value) { this._completionState = value }

  @Output() handlebarAction = new EventEmitter<Event>()
  onHandlebarAction(e: Event) { this.handlebarAction.emit(e) }

  @Input() tiles: LessonTile[] = []
  @Output() tilesChange = new EventEmitter<LessonTile[]>()

  get navigation(): NavigationBarState {
    return { current: { route: 'home', type: 'root' }, title: '', segments: [{ path: '', title: '' }] }
  }

  private _overall?: OverallStats
  get overall() { return this._overall }
  set overall(value) { this._overall = value }

  private _lesson?: LessonTile
  get lesson() { return this.state.contentState.lesson }
  set lesson(value) {
    if (!this.state?.contentState) { return }
    this.state.contentState.lesson = value ? { ...value, activeDate: this.activeDate } : undefined
    this.state.contentState.selected = this._lesson?.title
    this.stateChange.emit(this._state)
  }

  private _close = false
  get close() { return this._close }
  set close(value) {
    this._close = value
    this.lesson = undefined
  }

  private _dayPlan?: DayPlan
  get dayPlan() { return this._dayPlan }
  set dayPlan(plan: DayPlan | undefined) {
    this._dayPlan = plan
    if (plan) {
      this.updateCompletionState(plan) // Update completion state when day plan changes
      this.estimatedCompletionTime = Math.min(this.dayPlan?.estimatedCompletionTime ?? 0, this.dayPlanModules?.reduce((t, m) => t + (m?.revisedCompletionTime ?? 0), 0))
    }
  }

  private _dayPlanModules: DayPlanModule[] = []
  get dayPlanModules() { return this._dayPlanModules }
  set dayPlanModules(modules: DayPlanModule[]) {
    if (this.dayPlan) {
      this._dayPlanModules = modules // Sort and partition modules
      this.updateStateWithModules() // Update state with the new modules
      this.search.keywords = this._dayPlanModules.map(m => m.moduleTitle)
      this.estimatedCompletionTime = Math.min(this.dayPlan?.estimatedCompletionTime ?? 0, this.dayPlanModules?.reduce((t, m) => t + (m?.revisedCompletionTime ?? 0), 0))
    }
  }

  readonly studyService = inject(StudyService)
  readonly modalService = inject(BsModalService)

  constructor() {
    super()
    super.rootClassName = 'home-root-class-name'
  }

  override ngOnInit() {
    super.ngOnInit()
    this.getData(new DateOnly()) // Fetch data for the current date on initialization
  }

  // Handle changes in the search state
  override handleSearchChange(search: SearchState, previousSearch: SearchState = this.search) {
    const isNew = search.selected && search.selected !== previousSearch.selected
    if (isNew) {
      const found = this.state.contentState.tiles?.find(t => t.title === search.selected)
      log.debug(`${search.selected} ${found ? '' : 'not '}found ${found ? 'in ' : ''}${found?.title ?? ''}`)
      if (found?.action) {
        found.action('study').finally(() => search.selected = undefined)
      }
    }
  }

  // Update the completion state based on the current day plan
  private updateCompletionState(plan: DayPlan) {
    const today = this.studyService.completedPercentForDate(plan.dateOnly)
    const overall = this.overall?.progress ?? 0
    const completion = {
      activeDate: plan.dateOnly,
      studyTime: plan.actualStudyTime,
      estimated: plan.estimatedCompletionTime,
      completed: { today, overall }
    }
    this.completionState = {
      ...this.completionState,
      ...completion,
      selected: this.activeDate.isToday() ? 'today' : 'overall'
    }
  }

  // Update the component state with the sorted modules
  private updateStateWithModules() {
    if (!this.dayPlan) return
    this.state = this.createState(this.state, this.dayPlan, this._dayPlanModules, this.activeDate)
    const newTiles = (this.state.contentState.tiles ?? []) as LessonTile[]
    this.tiles = newTiles
    this.tilesChange.emit(newTiles)
  }

  private lessonTileService = inject(LessonTileService);

  // Create a new state for the drawer component
  private createState(state: DrawerState, plan: DayPlan, modules: DayPlanModule[], activeDate: DateOnly): DrawerState {
    // Only recreate tiles if modules have changed
    const tiles = ((this.state?.contentState?.tiles ?? []).length === modules.length) && DayPlanModule.isEqual(modules, this.dayPlanModules)
    ? this.state.contentState.tiles
    : modules.map(m => this.lessonTileService.createLessonTile(
        m,
        {
          submitted: this.submitted,
          detailAction: this.createDetailAction(m.moduleId ?? m.id),
          onHide: () => { this.lesson = undefined }
        }
      ));

    return {
      ...state,
      type: 'dayplan-lessons',
      headerState: {
        ...state?.headerState,
        tabs: this.createTabs(plan, modules, activeDate),
        activeTab: _isNewDesign ? 'All' : 'Active'
      },
      contentState: {
        ...state?.contentState,
        selected: undefined,
        date: plan?.dateOnly,
        tiles
      }
    };
  }

  // Create tabs for the drawer based on the plan and modules
  private createTabs(plan: DayPlan, modules: DayPlanModule[], activeDate: DateOnly) {
    const createSubtitle = () => plan?.dateOnly.isSameOrAfter(new DateOnly())
      ? plan?.isComplete ? 'Session complete' : `Estimated time: ${durationFormatter(this.estimatedCompletionTime, {short:false})}`
      : plan?.actualStudyTime ?? 0 > 0 ? `Session duration: ${durationFormatter(plan?.actualStudyTime, {short:false})}` : ''


    const date = plan.dateOnly
    const title = `${date.isToday() ? 'Today' : date.weekday()}'s Session`
    return _isNewDesign
      ? [{ disabled: !plan.id, label: 'All', hideLabel: true, title, subtitle: createSubtitle }]
      : [
        {
          disabled: !plan.id,
          label: `Active`,
          title,
          subtitle: createSubtitle
        },
        {
          disabled: !plan.id || activeDate.isAfter(new DateOnly()),
          label: `Done`,
          title,
          subtitle: () => plan?.dateOnly.isToday()
            ? modules.find(m => (m.isFinished || m.progress >= 1) && (plan?.actualStudyTime ?? 0) > 0) ? `Completed in ${durationFormatter(plan?.actualStudyTime, {short:false})}` : 'Incomplete'
            : ''
        }
      ]
  }

  private _estimatedCompletionTime = 0
  private get estimatedCompletionTime() {
    return this._estimatedCompletionTime
  }
  private set estimatedCompletionTime(value) {
    this._estimatedCompletionTime = value
  }

  // Display lesson detail
  private createDetailAction = (id: string | undefined) => () => {
    this.lesson = this.state.contentState.tiles?.find(t => t.id === id) as LessonTile
    return Promise.resolve(!!this.lesson)
  }

  // Fetch data for the specified date
  private getData(date: DateOnly) {
    if (this.isLoading(date)) { return }
    // Check cache first
    const cacheKey = date.toISOString();
    if (this.dayPlanCache.has(cacheKey)) {
      const cachedData = this.dayPlanCache.get(cacheKey);
      if (cachedData) { this.updateState(cachedData, date); }
      return;
    }
    this._isLoading = date
    this.onCancel.next()
    log.trace(`refreshing plan and modules for ${date.toISOString()}`)
    this.studyService.refreshDayPlanAndModules(date)
      .pipe(
        finalize(() => {
          performance.mark('getData-end');
          performance.measure('getData', 'getData-start', 'getData-end');
        }),
        takeUntil(merge(this.onDestroy, this.onCancel))
      )
      .subscribe((dayPlanAnModules: DayPlanAndModules | undefined) => {
        log.trace(`refreshed plan and modules for ${date.toISOString()}`, dayPlanAnModules)
        if (!dayPlanAnModules || !dayPlanAnModules.dateOnly.isSame(date) || this.modalService.getModalsCount() > 0) { return }
        // Cache the result
        this.dayPlanCache.set(cacheKey, dayPlanAnModules);
        this._isLoading = new DateOnly(distantPast)
        this.lesson = undefined
        this.updateState(dayPlanAnModules, date)
      })
  }

  // Update the component state with the fetched data
  private updateState(dayPlanAnModules: DayPlanAndModules, date: DateOnly) {
    const { plan, modules, overall } = dayPlanAnModules
    if (!OverallStats.isEqual(overall, this.overall)) {
      log.debug(`overall stats for ${date.toISOString()}`, overall)
      this.overall = overall
    }
    if (!DayPlan.isEqual(plan, this.dayPlan)) {
      log.debug(`day plan for ${date.toISOString()}`, plan)
      this.dayPlan = plan
    }
    if (!DayPlanModule.isEqual(modules, this.dayPlanModules)) {
      log.debug(`modules for ${date.toISOString()}`, modules)
      this.dayPlanModules = modules
    }
  }

  // Check if data is currently being loaded for the specified date
  private isLoading(date: DateOnly) {
    return date.isSame(this._isLoading)
  }

  // Getter to check if data is loaded for the active date
  get isLoaded() {
    return !!this.dayPlan?.dateOnly.isSame(this.activeDate) || !navigator.onLine
  }
}
